<template>
  <section
    v-if="previewFile"
    :class="['preview', { 'preview--fullscreen': fullscreen }]"
    @click="openPreviewModal"
    @mouseenter="setPreviewHovered(true)"
    @mouseleave="setPreviewHovered(false)"
  >
    <button
      v-if="
        fullscreen &&
        previewHovered &&
        !loadingFile &&
        !showPreviewFailedContainer &&
        previewType !== 'none'
      "
      class="preview__button"
    >
      <IconFileSearch2 class="preview__button__icon" />
    </button>
    <PulseLoader
      v-if="loadingFile && !showPreviewFailedContainer"
      class="preview__loading-spinner"
      color="#3f46d6"
      size="16px"
    />
    <VuePdfEmbed
      v-else-if="
        previewType === 'pdf' &&
        fullscreen &&
        !loadingFile &&
        !showPreviewFailedContainer &&
        fileBlobSource
      "
      class="preview__pdf"
      :class="{ preview__pdf__hover: previewHovered }"
      :page="1"
      :source="fileBlobSource"
      :width="700"
    />
    <VuePdfEmbed
      v-else-if="
        previewType === 'pdf' &&
        !fullscreen &&
        !loadingFile &&
        !showPreviewFailedContainer &&
        fileBlobSource
      "
      class="preview__overflow-hidden__pdf"
      :source="fileBlobSource"
      :width="700"
      :page="pdfCurrentPage"
      @loaded="setNumPages"
      @error="handlePdfError"
    />
    <div
      v-else-if="
        previewType === 'text' && !loadingFile && !showPreviewFailedContainer && textContent
      "
      class="preview__text"
    >
      {{ textContent }}
    </div>
    <div
      v-else-if="
        previewType === 'markdown' && !loadingFile && !showPreviewFailedContainer && textContent
      "
      class="preview__text"
    >
      <MarkDown :source="textContent || t('general__text__preview-not-available-for-file-type')" />
    </div>
    <img
      v-else-if="previewType === 'image' && !loadingFile && !showPreviewFailedContainer"
      class="preview__image"
      :class="{ preview__image__hover: previewHovered }"
      :src="fileBlobSource"
      :alt="t('general__text__preview-click-to')"
    />
    <video
      v-else-if="previewType === 'video' && !loadingFile && !showPreviewFailedContainer"
      :key="fileBlobSource"
      class="preview__image"
      controls
      autoplay
      :src="fileBlobSource"
    >
      {{ t('general__text__preview-not-available-for-file-type') }}
    </video>
    <div v-else class="no-preview">
      <IconMediumEyeOff />
      <p class="no-preview__text">
        {{
          (pdfError ? pdfError : fileErrorText) ??
          t('general__text__preview-not-available-for-file-type')
        }}.
      </p>
    </div>
  </section>
</template>
<script setup lang="ts">
import VuePdfEmbed from 'vue-pdf-embed'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import { useI18n } from 'vue-i18n'
import { computed, ref, toRefs, watch } from 'vue'
import { DirectoryItem } from './types'
import IconMediumEyeOff from '@/design-system/assets/icons/IconMediumEyeOff.vue'
import IconFileSearch2 from '@/design-system/assets/icons/IconFileSearch2.vue'
import MarkDown from '@/components/MarkDown.vue'
import { PreviewType } from './useFilePreview'
import type { PDFDocumentProxy } from 'pdfjs-dist/types/web/pdf_viewer'

type Props = {
  newPage?: number
  fullscreen?: boolean
  previewFile: DirectoryItem
  previewType: PreviewType
  fileBlobSource: string | undefined
  textContent: string | undefined
  loadingFile: boolean
  fileErrorText: string | undefined
}
const props = withDefaults(defineProps<Props>(), {
  previewFile: undefined,
  newPage: undefined,
  previewType: 'none',
  fullscreen: false
})
const {
  newPage,
  previewFile,
  previewType,
  fileBlobSource,
  textContent,
  loadingFile,
  fileErrorText
} = toRefs(props)

const emit = defineEmits<{
  (e: 'preview-file', previewFile: DirectoryItem): void
  (e: 'pdf-numpages', pdfNumPages: number): void
  (e: 'pdf-page', pdfCurrentPage: number): void
  (e: 'pdf-error', pdfError: string | null): void
}>()

const { t } = useI18n()
const previewHovered = ref(false)
const pdfNumPages = ref(1)
const pdfCurrentPage = ref(1)
const pdfError = ref<string | null>(null)
const filePreviewFailed = ref(false)
const showPreviewFailedContainer = computed(() => {
  return pdfError.value != null || filePreviewFailed.value
})

function openPreviewModal() {
  if (previewFile.value) {
    emit('preview-file', previewFile.value)
  }
}
function setPreviewHovered(hovered: boolean) {
  previewHovered.value = hovered
}

function handlePdfError(error: any) {
  let errorMessage = 'PDF rendering failed.'
  if (error && error.message) {
    errorMessage = error.message
  } else if (typeof error === 'string') {
    errorMessage = error
  }
  pdfError.value = errorMessage
}
watch(
  pdfCurrentPage,
  (newPage) => {
    emit('pdf-page', newPage)
  },
  { immediate: true }
)
watch(
  pdfNumPages,
  (newNumPages) => {
    emit('pdf-numpages', newNumPages)
  },
  { immediate: true }
)
watch(
  newPage,
  (newPageValue) => {
    if (newPageValue && newPageValue > 0) {
      pdfCurrentPage.value = newPageValue
    }
  },
  { immediate: true, deep: true }
)
watch(
  pdfError,
  (newError) => {
    emit('pdf-error', newError)
  },
  { immediate: true }
)

function setNumPages(pdf: PDFDocumentProxy) {
  pdfNumPages.value = pdf.numPages
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';
.preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  text-align: center;
  overflow: hidden;
  max-height: 65vh;
  width: 100%;
  min-height: 100px;

  &__button {
    all: unset;
    height: 40px;
    position: absolute;
    width: 40px;
    z-index: 2;
    &__icon {
      color: variables.$color-pure-white;
      cursor: pointer;
    }
  }
  &--fullscreen {
    align-items: center;
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
    position: relative;
    text-align: center;
    z-index: 1;
    max-height: 60%;
    .preview__image {
      height: intrinsic; // Possibly solves stretched image issue in Safari
      margin: auto;
      max-height: 100%;
      max-width: 374px;
      object-fit: contain;
      width: 100%;
      @include font-mixins.desktop-regular-paragraph;

      &__hover {
        background-color: rgba(#1a1a1a, 0.5);
        cursor: pointer;
        filter: brightness(50%);
      }
    }
  }
  &__image {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 16px 24px 16px 0;
    max-width: calc(100% - 32px);
    max-height: calc(100% - 32px);
    overflow: hidden;
  }
  &__text {
    color: variables.$color-greyscale-content;
    text-align: left;
    width: 100%;
    height: 100%;
    padding: 16px;
  }
  &__loading-spinner {
    margin: 56px auto;
  }
  &__pdf {
    max-height: 100%;
    max-width: 374px;
    object-fit: contain;
    overflow: hidden;
    padding-bottom: 4px; // Necessary for correct scroll bar activation
    @include mixins.scrollbar-vertical;
    &__hover {
      background-color: rgba(#1a1a1a, 0.5);
      cursor: pointer;
      filter: brightness(50%);
    }
  }
  &__overflow-hidden {
    overflow: hidden;
    &__pdf {
      width: 100%;
      height: 100%;
      overflow: auto;
      margin: 2px;
      @include mixins.scrollbar-horizontal;
      @include mixins.scrollbar-vertical;
    }
  }
  .no-preview {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    padding: 0px 24px;
    border: 1px solid variables.$color-greyscale-line-grey;
    background: variables.$color-greyscale-background;
    min-width: 444px;
    height: 594px;
    margin: 16px 24px 16px 0px;
    &__text {
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      color: variables.$color-greyscale-content;
      @include font-mixins.desktop-bold-paragraph;
      max-width: 396px;
      margin: 0;
    }
  }
}
</style>
